import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Search, X, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import jsPDF from 'jspdf';

export default function OwnerForm() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertySearch, setPropertySearch] = useState('');

  const [authorizationData, setAuthorizationData] = useState({
    property_id: '',
    codigo_imovel: '',
    proprietario: '',
    cpf: '',
    rg: '',
    data_nascimento: '',
    profissao: '',
    email: '',
    endereco_residencial: '',
    bairro_residencial: '',
    cep_residencial: '',
    cidade_residencial: '',
    uf_residencial: '',
    telefone: '',
    celular: '',
    endereco_comercial: '',
    bairro_comercial: '',
    cep_comercial: '',
    cidade_comercial: '',
    uf_comercial: '',
    telefone_comercial: '',
    celular_comercial: '',
    valor_avaliacao: '',
    valor_pretendido: '',
    taxa_intermediacao: '',
    quadra: '',
    lote: '',
    matricula: '',
    observacoes: '',
    });

  const [formData, setFormData] = useState({
    authorizations: [],
    property_ids: [],
    created_at: '',
    updated_at: '',
    name: '',
    cpf: '',
    rg: '',
    birth_date: '',
    profession: '',
    email: '',
    phone: '',
    mobile: '',
    residential_address: {
      street: '',
      neighborhood: '',
      zip_code: '',
      city: '',
      state: '',
    },
    commercial_address: {
      street: '',
      neighborhood: '',
      zip_code: '',
      city: '',
      state: '',
    },
    commercial_phone: '',
    commercial_mobile: '',
    notes: '',
  });

  const formatLongDateBR = (date = new Date()) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString('pt-BR', { month: 'long' });
    const year = date.getFullYear();

    return `${day} de ${month} de ${year}`;
  };

  const fetchProperties = useCallback(async () => {
    try {
      const response = await api.get('/properties?limit=1000');
      setProperties(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
      toast.error('Erro ao carregar imóveis.');
    }
  }, [api]);

  const fetchOwner = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await api.get(`/owners/${id}`);
      setFormData({
        authorizations: response.data.authorizations || [],
        property_ids: response.data.property_ids || [],
        created_at: response.data.created_at || '',
        updated_at: response.data.updated_at || '',
        name: response.data.name || '',
        cpf: response.data.cpf || '',
        rg: response.data.rg || '',
        birth_date: response.data.birth_date || '',
        profession: response.data.profession || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        mobile: response.data.mobile || '',
        residential_address: {
          street: response.data.residential_address?.street || '',
          neighborhood: response.data.residential_address?.neighborhood || '',
          zip_code: response.data.residential_address?.zip_code || '',
          city: response.data.residential_address?.city || '',
          state: response.data.residential_address?.state || '',
        },
        commercial_address: {
          street: response.data.commercial_address?.street || '',
          neighborhood: response.data.commercial_address?.neighborhood || '',
          zip_code: response.data.commercial_address?.zip_code || '',
          city: response.data.commercial_address?.city || '',
          state: response.data.commercial_address?.state || '',
        },
        commercial_phone: response.data.commercial_phone || '',
        commercial_mobile: response.data.commercial_mobile || '',
        notes: response.data.notes || '',
      });
    } catch (error) {
      toast.error('Erro ao carregar proprietário.');
      navigate('/admin/owners');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  useEffect(() => {
    fetchProperties();
    fetchOwner();
  }, [fetchProperties, fetchOwner]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const formatCurrencyBRL = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = typeof value === 'number'
      ? value
      : Number(String(value).replace(/\D/g, '')) / 100;

    if (Number.isNaN(number)) return '';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(number);
  };

  const parseCurrencyBRL = (value) => {
    if (!value) return 0;
    const cleaned = String(value).replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '');
    const parsed = Number(cleaned);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Percentual: aceita apenas dígitos e uma vírgula decimal (ex: 5 ou 5,5)
  const sanitizePercent = (value) => {
    const cleaned = String(value || '').replace(/\./g, ',').replace(/[^\d,]/g, '');
    const [intPart, ...decParts] = cleaned.split(',');
    return decParts.length ? `${intPart},${decParts.join('').slice(0, 2)}` : intPart;
  };

  const parsePercent = (value) => {
    if (!value) return 0;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return String(value).replace('.', ',');
  };

  const formatDateBR = (dateString) => {
    if (!dateString) return '';

    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }

    return dateString;
  };

  const togglePropertySelection = (propertyId) => {
    setFormData((prev) => {
      const exists = prev.property_ids.includes(propertyId);
      return {
        ...prev,
        property_ids: exists
          ? prev.property_ids.filter((id) => id !== propertyId)
          : [...prev.property_ids, propertyId],
      };
    });
  };

  const handleAuthorizationPropertyChange = (propertyId) => {
    const property = properties.find((p) => p.id === propertyId);
    const existingAuthorization =
      (formData.authorizations || []).find((item) => item.property_id === propertyId) || null;

    setAuthorizationData({
      property_id: propertyId,
      codigo_imovel: property?.id || '',
      proprietario: formData.name || '',
      cpf: formData.cpf || '',
      rg: formData.rg || '',
      data_nascimento: formatDateBR(formData.birth_date),
      profissao: formData.profession || '',
      email: formData.email || '',
      endereco_residencial: formData.residential_address?.street || '',
      bairro_residencial: formData.residential_address?.neighborhood || '',
      cep_residencial: formData.residential_address?.zip_code || '',
      cidade_residencial: formData.residential_address?.city || '',
      uf_residencial: formData.residential_address?.state || '',
      telefone: formData.phone || '',
      celular: formData.mobile || '',
      endereco_comercial: formData.commercial_address?.street || '',
      bairro_comercial: formData.commercial_address?.neighborhood || '',
      cep_comercial: formData.commercial_address?.zip_code || '',
      cidade_comercial: formData.commercial_address?.city || '',
      uf_comercial: formData.commercial_address?.state || '',
      telefone_comercial: formData.commercial_phone || '',
      celular_comercial: formData.commercial_mobile || '',
      valor_avaliacao: existingAuthorization?.valor_avaliacao
        ? formatCurrencyBRL(existingAuthorization.valor_avaliacao)
        : '',
      valor_pretendido: existingAuthorization?.valor_pretendido
        ? formatCurrencyBRL(existingAuthorization.valor_pretendido)
        : '',
      taxa_intermediacao: existingAuthorization?.taxa_intermediacao
        ? formatPercent(existingAuthorization.taxa_intermediacao)
        : '',
      quadra: existingAuthorization?.quadra || '',
      lote: existingAuthorization?.lote || '',
      matricula: existingAuthorization?.matricula || '',
      observacoes: existingAuthorization?.observacoes || property?.description || '',
    });
  };

  
  const drawFieldBox = (doc, x, y, width, height = 8) => {
    doc.rect(x, y, width, height);
  };

  const drawGrayTitleBox = (doc, title, x, y, width, height = 8) => {
    doc.setFillColor(230, 230, 230);
    doc.rect(x, y, width, height, 'F');
    doc.rect(x, y, width, height);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);

    // CENTRALIZADO
    doc.text(title, x + width / 2, y + 5.5, { align: 'center' });
  };

  const loadImageAsDataURL = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = reject;
      img.src = src;
    });
  
  const generateAuthorizationPDF = async () => {
    if (!authorizationData.property_id || !selectedAuthorizationProperty) {
      toast.error('Selecione um imóvel para gerar a autorização.');
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    try {
      const logoData = await loadImageAsDataURL('/logo.png');

      // logo no topo esquerdo
      const imgProps = doc.getImageProperties(logoData);
      const pdfWidth = 30;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      const logoX = 14;
      const logoY = 8;

      doc.addImage(logoData, 'PNG', logoX, logoY, pdfWidth, pdfHeight);

      // mais espaço abaixo da logo
      let y = logoY + pdfHeight + 8;
      
    } catch (error) {
      console.error('Erro ao carregar logo para o PDF:', error);
    }

    let y = 30;

    // TÍTULO DA SEÇÃO
    drawGrayTitleBox(doc, 'AUTORIZAÇÃO' , 14, y, 182, 8);
    y += 8;

    // CÓDIGO DO IMÓVEL
    drawFieldBox(doc, 14, y, 182, 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`CÓDIGO DO IMÓVEL: ${authorizationData.codigo_imovel || ''}`, 16, y + 5.5);
    y += 8;

    // PROPRIETÁRIO
    drawFieldBox(doc, 14, y, 182, 8);
    doc.text(`PROPRIETÁRIO: ${authorizationData.proprietario || ''}`, 16, y + 5.5);
    y += 8;

    // CPF | RG | DATA NASCIMENTO
    drawFieldBox(doc, 14, y, 60, 8);   // CPF menor
    drawFieldBox(doc, 74, y, 50, 8);   // RG menor
    drawFieldBox(doc, 124, y, 72, 8);  // DATA maior

    doc.text(`CPF: ${authorizationData.cpf || ''}`, 16, y + 5.5);
    doc.text(`RG: ${authorizationData.rg || ''}`, 76, y + 5.5);
    doc.text(`NASCIMENTO: ${authorizationData.data_nascimento || ''}`, 126, y + 5.5);
    y += 8;

    // PROFISSÃO | E-MAIL
    drawFieldBox(doc, 14, y, 90, 8);
    drawFieldBox(doc, 104, y, 92, 8);

    doc.text(`PROFISSÃO: ${authorizationData.profissao || ''}`, 16, y + 5.5);
    doc.text(`E-MAIL: ${authorizationData.email || ''}`, 106, y + 5.5);
    y += 10;

    // ENDEREÇO RESIDENCIAL
    drawFieldBox(doc, 14, y, 182, 8);
    doc.setFont('helvetica', 'normal');
    doc.text(`ENDEREÇO RESIDENCIAL: ${authorizationData.endereco_residencial || ''}`, 16, y + 5.5);
    y += 8;

    // BAIRRO | CEP | CIDADE | UF
    drawFieldBox(doc, 14, y, 70, 8);
    drawFieldBox(doc, 84, y, 35, 8);
    drawFieldBox(doc, 119, y, 59, 8);
    drawFieldBox(doc, 178, y, 18, 8);

    doc.text(`BAIRRO: ${authorizationData.bairro_residencial || ''}`, 16, y + 5.5);
    doc.text(`CEP: ${authorizationData.cep_residencial || ''}`, 86, y + 5.5);
    doc.text(`CIDADE: ${authorizationData.cidade_residencial || ''}`, 121, y + 5.5);
    doc.text(`UF: ${authorizationData.uf_residencial || ''}`, 180, y + 5.5);
    y += 8;

    // TELEFONE | CELULAR
    drawFieldBox(doc, 14, y, 91, 8);
    drawFieldBox(doc, 105, y, 91, 8);

    doc.text(`TELEFONE: ${authorizationData.telefone || ''}`, 16, y + 5.5);
    doc.text(`CELULAR: ${authorizationData.celular || ''}`, 107, y + 5.5);
    y += 10;

    const hasCommercialAddress =
      authorizationData.endereco_comercial ||
      authorizationData.bairro_comercial ||
      authorizationData.cidade_comercial ||
      authorizationData.telefone_comercial ||
      authorizationData.celular_comercial;

    if (hasCommercialAddress) {
      y += 2;

      // ENDEREÇO COMERCIAL
      drawFieldBox(doc, 14, y, 182, 8);
      doc.text(`ENDEREÇO COMERCIAL: ${authorizationData.endereco_comercial || ''}`, 16, y + 5.5);
      y += 8;

      // BAIRRO | CEP | CIDADE | UF
      drawFieldBox(doc, 14, y, 70, 8);
      drawFieldBox(doc, 84, y, 35, 8);
      drawFieldBox(doc, 119, y, 59, 8);
      drawFieldBox(doc, 178, y, 18, 8);

      doc.text(`BAIRRO: ${authorizationData.bairro_comercial || ''}`, 16, y + 5.5);
      doc.text(`CEP: ${authorizationData.cep_comercial || ''}`, 86, y + 5.5);
      doc.text(`CIDADE: ${authorizationData.cidade_comercial || ''}`, 121, y + 5.5);
      doc.text(`UF: ${authorizationData.uf_comercial || ''}`, 180, y + 5.5);
      y += 8;

      // TELEFONE | CELULAR
      drawFieldBox(doc, 14, y, 91, 8);
      drawFieldBox(doc, 105, y, 91, 8);

      doc.text(`TELEFONE: ${authorizationData.telefone_comercial || ''}`, 16, y + 5.5);
      doc.text(`CELULAR: ${authorizationData.celular_comercial || ''}`, 107, y + 5.5);
      y += 10;
    }

    y += 1;

    // TÍTULO DA SEÇÃO
    drawGrayTitleBox(doc, 'DADOS DO IMÓVEL', 14, y, 182, 8);
    y += 8;

    // TÍTULO + ANO DE CONSTRUÇÃO
    drawFieldBox(doc, 14, y, 182, 8);

    // Título (lado esquerdo)
    doc.text(
      `TÍTULO: ${selectedAuthorizationProperty.title || ''}`,
      16,
      y + 5.5
    );

    // Ano de construção (lado direito)
    if (selectedAuthorizationProperty.construction_year) {
      doc.text(
        `ANO: ${selectedAuthorizationProperty.construction_year}`,
        194,
        y + 5.5,
        { align: 'right' }
      );
    }

    y += 8;

    // ENDEREÇO
    drawFieldBox(doc, 14, y, 182, 8);
    doc.text(
      `ENDEREÇO: ${selectedAuthorizationProperty.address?.street || ''}, ${selectedAuthorizationProperty.address?.number || ''}`,
      16,
      y + 5.5
    );
    y += 8;

    // BAIRRO | CEP | CIDADE | UF
    drawFieldBox(doc, 14, y, 70, 8);
    drawFieldBox(doc, 84, y, 35, 8);
    drawFieldBox(doc, 119, y, 59, 8);
    drawFieldBox(doc, 178, y, 18, 8);

    doc.text(`BAIRRO: ${selectedAuthorizationProperty.address?.neighborhood || ''}`, 16, y + 5.5);
    doc.text(`CEP: ${selectedAuthorizationProperty.address?.zip_code || ''}`, 86, y + 5.5);
    doc.text(`CIDADE: ${selectedAuthorizationProperty.address?.city || ''}`, 121, y + 5.5);
    doc.text(`UF: ${selectedAuthorizationProperty.address?.state || ''}`, 180, y + 5.5);
    y += 8;

    // ÁREA PRIVATIVA | ÁREA TOTAL
    drawFieldBox(doc, 14, y, 91, 8);
    drawFieldBox(doc, 105, y, 91, 8);

    doc.text(`ÁREA PRIVATIVA: ${selectedAuthorizationProperty.sizes?.built_area_m2 || ''} m²`, 16, y + 5.5);
    doc.text(`ÁREA TOTAL: ${selectedAuthorizationProperty.sizes?.total_area_m2 || ''} m²`, 107, y + 5.5);
    y += 8;

    // QUADRA | LOTE | MATRÍCULA
    drawFieldBox(doc, 14, y, 45, 8);
    drawFieldBox(doc, 59, y, 35, 8);
    drawFieldBox(doc, 94, y, 102, 8);

    doc.text(`QUADRA: ${authorizationData.quadra || ''}`, 16, y + 5.5);
    doc.text(`LOTE: ${authorizationData.lote || ''}`, 61, y + 5.5);
    doc.text(`MATRÍCULA: ${authorizationData.matricula || ''}`, 96, y + 5.5);
    y += 8;

    // VALOR AVALIAÇÃO | VALOR PRETENDIDO
    drawFieldBox(doc, 14, y, 91, 8);
    drawFieldBox(doc, 105, y, 91, 8);

    doc.text(`VALOR AVALIAÇÃO: ${authorizationData.valor_avaliacao || ''}`, 16, y + 5.5);
    doc.text(`VALOR PRETENDIDO: ${authorizationData.valor_pretendido || ''}`, 107, y + 5.5);
    y += 8;

    // TAXA DE INTERMEDIAÇÃO
    drawFieldBox(doc, 14, y, 182, 8);
    doc.text(
      `TAXA DE INTERMEDIAÇÃO: ${authorizationData.taxa_intermediacao ? `${authorizationData.taxa_intermediacao}%` : ''}`,
      16,
      y + 5.5
    );
    y += 10;

    y += 1;

    // TÍTULO DA SEÇÃO
    drawGrayTitleBox(doc, 'COMODIDADES', 14, y, 182, 8);
    y += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    const amenitiesText =
      getAuthorizationAmenities(selectedAuthorizationProperty).join(', ') || 'Nenhuma informada';

    const amenitiesLines = doc.splitTextToSize(amenitiesText, 176);
    const amenitiesBoxHeight = Math.max(12, amenitiesLines.length * 5 + 4);

    // CAIXA DAS COMODIDADES
    drawFieldBox(doc, 14, y, 182, amenitiesBoxHeight);
    doc.text(amenitiesLines, 16, y + 5.5);

    y += amenitiesBoxHeight + 10;
    
    y += 2;
    doc.setFont('helvetica', 'bold');
    doc.text('OBSERVAÇÕES', 14, y);
    y += 6;
    doc.setFont('helvetica', 'normal');

    const observacoes = doc.splitTextToSize(authorizationData.observacoes || '', 180);
    doc.text(observacoes, 14, y);

    y += Math.max(20, observacoes.length * 5 + 8);

    doc.setFont('helvetica', 'bold');
    doc.text('AUTORIZAÇÃO:', 14, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    const textoAutorizacao = doc.splitTextToSize(
      'AUTORIZO a VALMIR CAPELETTI - NEGÓCIOS IMOBILIÁRIOS - CNPJ 53.814.056/0001-46 - divulgar e anunciar pelo prazo de 6 meses a contar da data da assinatura desta autorização em jornais, revistas e nos meios digitais o imóvel de minha propriedade descrito nesta ficha cadastral.',
      180
    );
    doc.text(textoAutorizacao, 14, y);

    const authorizationDate = formatLongDateBR();

    y += textoAutorizacao.length * 4 + 4;
    doc.text(`TOLEDO, PR, ${authorizationDate}.`, 14, y);

    y += 24;
    doc.text('____________________________________', 19, y);
    doc.text(authorizationData.proprietario || '', 55, y + 6, { align: 'center' });
    
    doc.setFontSize(9);
    doc.text('PROPRIETÁRIO', 55, y + 11, { align: 'center' });

    // linha
    doc.text('____________________________________', 107, y);

    // nome
    doc.text('VALMIR CAPELETTI', 143, y + 6, { align: 'center' });

    // subtítulo
    doc.setFontSize(9);
    doc.text('NEGÓCIOS IMOBILIÁRIOS', 143, y + 11, { align: 'center' });

    // volta tamanho padrão
    doc.setFontSize(10);

    doc.save(`autorizacao-${authorizationData.codigo_imovel || 'imovel'}.pdf`);
  };


  const handleSaveAuthorization = async () => {
    if (!id) {
      toast.error('Salve o proprietário antes de registrar a autorização.');
      return;
    }

    if (!authorizationData.property_id) {
      toast.error('Selecione um imóvel.');
      return;
    }

    try {
      const payload = {
        property_id: authorizationData.property_id,
        valor_avaliacao: parseCurrencyBRL(authorizationData.valor_avaliacao),
        valor_pretendido: parseCurrencyBRL(authorizationData.valor_pretendido),
        taxa_intermediacao: parsePercent(authorizationData.taxa_intermediacao),
        quadra: authorizationData.quadra || '',
        lote: authorizationData.lote || '',
        matricula: authorizationData.matricula || '',
        observacoes: authorizationData.observacoes || '',
        updated_at: new Date().toISOString(),
      };

      await api.post(`/owners/${id}/authorization`, payload);

      const updatedAuthorizations = [
        ...(formData.authorizations || []).filter(
          (item) => item.property_id !== authorizationData.property_id
        ),
        payload,
      ];

      setFormData((prev) => ({
        ...prev,
        authorizations: updatedAuthorizations,
      }));

      toast.success('Autorização salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar autorização:', error);
      toast.error('Erro ao salvar autorização.');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Nome é obrigatório.');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/owners/${id}`, formData);
        toast.success('Proprietário atualizado com sucesso!');
      } else {
        await api.post('/owners', formData);
        toast.success('Proprietário cadastrado com sucesso!');
      }
      navigate('/admin/owners');
    } catch (error) {
      console.error('Erro ao salvar proprietário:', error);
      console.error('Resposta do backend:', error.response?.data);

      toast.error(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          'Erro ao salvar proprietário.'
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedProperties = properties.filter((property) =>
    formData.property_ids.includes(property.id)
  );

  const availableProperties = properties.filter((property) => {
    const alreadySelected = formData.property_ids.includes(property.id);
    if (alreadySelected) return false;

    const search = propertySearch.toLowerCase().trim();
    if (!search) return true;

    return (
      property.title?.toLowerCase().includes(search) ||
      property.id?.toLowerCase().includes(search) ||
      property.address?.neighborhood?.toLowerCase().includes(search)
    );
  });

  const ownerProperties = properties.filter((property) =>
    formData.property_ids.includes(property.id)
  );

  const selectedAuthorizationProperty =
    ownerProperties.find((property) => property.id === authorizationData.property_id) || null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const getAuthorizationAmenities = (property) => {
    if (!property) return [];

    const items = [];

    if (property.condition) {
      items.push(`Conservação: ${property.condition}`);
    }

    if (Array.isArray(property.amenities)) {
      items.push(...property.amenities);
    }

    if (property.furnished) items.push('Mobiliado');
    if (property.is_new) items.push('Novo');
    if (property.pet_friendly) items.push('Aceita Pets');
    if (property.accessibility) items.push('Acessibilidade');

    return [...new Set(items)];
  };

  return (
    <div className="space-y-6" data-testid="owner-form">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/owners')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">
            {isEditing ? 'Editar Proprietário' : 'Novo Proprietário'}
          </h1>
          {formData.created_at && (
            <p className="text-sm text-slate-500 mt-1">
              Cadastrado no CRM em:{' '}
              <span className="font-medium text-slate-700">
                {new Date(formData.created_at).toLocaleString('pt-BR', {
                  timeZone: 'America/Sao_Paulo'
                })}
              </span>
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Tabs defaultValue="dados" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="dados">Dados Pessoais</TabsTrigger>
            <TabsTrigger value="imoveis">Imóveis Angariados</TabsTrigger>
            <TabsTrigger value="autorizacao">Autorizações</TabsTrigger>
          </TabsList>

          <TabsContent value="dados" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Dados Pessoais</CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>CPF</Label>
                  <Input
                    value={formData.cpf}
                    onChange={(e) => handleChange('cpf', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>RG</Label>
                  <Input
                    value={formData.rg}
                    onChange={(e) => handleChange('rg', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Data de Nascimento</Label>
                  <Input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => handleChange('birth_date', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Profissão</Label>
                  <Input
                    value={formData.profession}
                    onChange={(e) => handleChange('profession', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Celular</Label>
                  <Input
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Endereço Residencial</CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Endereço Residencial</Label>
                  <Input
                    value={formData.residential_address.street}
                    onChange={(e) =>
                      handleAddressChange('residential_address', 'street', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Bairro</Label>
                  <Input
                    value={formData.residential_address.neighborhood}
                    onChange={(e) =>
                      handleAddressChange('residential_address', 'neighborhood', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>CEP</Label>
                  <Input
                    value={formData.residential_address.zip_code}
                    onChange={(e) =>
                      handleAddressChange('residential_address', 'zip_code', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Cidade</Label>
                  <Input
                    value={formData.residential_address.city}
                    onChange={(e) =>
                      handleAddressChange('residential_address', 'city', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>UF</Label>
                  <Input
                    value={formData.residential_address.state}
                    onChange={(e) =>
                      handleAddressChange('residential_address', 'state', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Endereço Comercial</CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Endereço Comercial</Label>
                  <Input
                    value={formData.commercial_address.street}
                    onChange={(e) =>
                      handleAddressChange('commercial_address', 'street', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Bairro</Label>
                  <Input
                    value={formData.commercial_address.neighborhood}
                    onChange={(e) =>
                      handleAddressChange('commercial_address', 'neighborhood', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>CEP</Label>
                  <Input
                    value={formData.commercial_address.zip_code}
                    onChange={(e) =>
                      handleAddressChange('commercial_address', 'zip_code', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Cidade</Label>
                  <Input
                    value={formData.commercial_address.city}
                    onChange={(e) =>
                      handleAddressChange('commercial_address', 'city', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>UF</Label>
                  <Input
                    value={formData.commercial_address.state}
                    onChange={(e) =>
                      handleAddressChange('commercial_address', 'state', e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Telefone Comercial</Label>
                  <Input
                    value={formData.commercial_phone}
                    onChange={(e) => handleChange('commercial_phone', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Celular Comercial</Label>
                  <Input
                    value={formData.commercial_mobile}
                    onChange={(e) => handleChange('commercial_mobile', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Observações</CardTitle>
              </CardHeader>
              <CardContent>
                <Label>Notas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={4}
                  className="mt-1"
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="imoveis" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Imóveis Vinculados</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {properties.length === 0 ? (
                  <p className="text-sm text-slate-500">Nenhum imóvel encontrado.</p>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Buscar imóvel por código, título ou bairro..."
                        value={propertySearch}
                        onChange={(e) => setPropertySearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    <div className="border rounded-lg max-h-72 overflow-y-auto divide-y">
                      {availableProperties.length === 0 ? (
                        <div className="p-4 text-sm text-slate-500">
                          Nenhum imóvel encontrado para esta busca.
                        </div>
                      ) : (
                        availableProperties.map((property) => (
                          <button
                            key={property.id}
                            type="button"
                            onClick={() => togglePropertySelection(property.id)}
                            className="w-full text-left p-4 hover:bg-slate-50 transition"
                          >
                            <p className="font-medium text-slate-800">{property.title}</p>
                            <p className="text-sm text-slate-500">
                              {property.id} • {property.address?.neighborhood || 'Sem bairro'}
                            </p>
                          </button>
                        ))
                      )}
                    </div>

                    <div>
                      <Label className="mb-2 block">Imóveis Selecionados</Label>

                      {selectedProperties.length === 0 ? (
                        <p className="text-sm text-slate-500">Nenhum imóvel selecionado.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {selectedProperties.map((property) => (
                            <div
                              key={property.id}
                              className="inline-flex items-center gap-2 rounded-full bg-brand-blue/10 text-brand-blue px-3 py-2 text-sm"
                            >
                              <span className="font-medium">{property.id}</span>
                              <span>— {property.title}</span>
                              <button
                                type="button"
                                onClick={() => togglePropertySelection(property.id)}
                                className="hover:text-red-500 transition"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="autorizacao" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Dados da Autorização</CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Selecione o Imóvel</Label>
                  <div className="mt-2 space-y-2">
                    {ownerProperties.length === 0 ? (
                      <p className="text-sm text-slate-500">Nenhum imóvel vinculado a este proprietário.</p>
                    ) : (
                      ownerProperties.map((property) => (
                        <label
                          key={property.id}
                          className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
                            authorizationData.property_id === property.id
                              ? 'border-brand-blue bg-brand-blue/5'
                              : 'border-slate-200'
                          }`}
                        >
                          <input
                            type="radio"
                            name="authorization_property"
                            checked={authorizationData.property_id === property.id}
                            onChange={() => handleAuthorizationPropertyChange(property.id)}
                          />
                          <div>
                            <p className="font-medium text-slate-800">{property.title}</p>
                            <p className="text-sm text-slate-500">
                              {property.id} • {property.address?.neighborhood || 'Sem bairro'}
                            </p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <Label>Valor Avaliação</Label>
                  <Input
                    value={authorizationData.valor_avaliacao}
                    onChange={(e) =>
                      setAuthorizationData((prev) => ({
                        ...prev,
                        valor_avaliacao: formatCurrencyBRL(e.target.value),
                      }))
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Valor Pretendido</Label>
                    <Input
                      value={authorizationData.valor_pretendido}
                      onChange={(e) =>
                        setAuthorizationData((prev) => ({
                          ...prev,
                          valor_pretendido: formatCurrencyBRL(e.target.value),
                        }))
                      }
                      className="mt-1"
                    />
                  </div>

                <div className="md:col-span-2">
                  <Label>Taxa de Intermediação (%)</Label>
                  <div className="relative mt-1">
                    <Input
                      inputMode="decimal"
                      placeholder="Ex: 5 ou 5,5"
                      value={authorizationData.taxa_intermediacao}
                      onChange={(e) =>
                        setAuthorizationData((prev) => ({
                          ...prev,
                          taxa_intermediacao: sanitizePercent(e.target.value),
                        }))
                      }
                      className="pr-8"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                      %
                    </span>
                  </div>
                </div>

                <div>
                  <Label>Quadra</Label>
                  <Input
                    value={authorizationData.quadra}
                    onChange={(e) =>
                      setAuthorizationData((prev) => ({ ...prev, quadra: e.target.value }))
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Lote</Label>
                  <Input
                    value={authorizationData.lote}
                    onChange={(e) =>
                      setAuthorizationData((prev) => ({ ...prev, lote: e.target.value }))
                    }
                    className="mt-1"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Matrícula</Label>
                  <Input
                    value={authorizationData.matricula}
                    onChange={(e) =>
                      setAuthorizationData((prev) => ({ ...prev, matricula: e.target.value }))
                    }
                    className="mt-1"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={authorizationData.observacoes}
                    onChange={(e) =>
                      setAuthorizationData((prev) => ({ ...prev, observacoes: e.target.value }))
                    }
                    rows={5}
                    className="mt-1"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSaveAuthorization}
                  >
                    Salvar Autorização
                  </Button>

                  <Button
                    type="button"
                    onClick={generateAuthorizationPDF}
                    className="bg-brand-blue hover:bg-brand-blue-dark"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Gerar Autorização PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/owners')}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-brand-blue hover:bg-brand-blue-dark"
          >
            {saving ? 'Salvando...' : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Atualizar' : 'Cadastrar'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}